/**
 * "Send to…": send a ONE task or note (or a fresh brief) to the apps that run that work — FusionTask, Mythos Task
 * Manager, Mythos Ticketing, more as each app opens its side. Several apps at once; the AI pre-selects and pre-fills,
 * the person checks and sends. Exports for other pages: SendToButton, LinkedWork, SendToAppDialog.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Link2, Send, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { api, post } from "../lib/api";
import { ErrorNote, Field, Modal } from "./ui";

export interface DispatchTarget {
  key: string;
  label: string;
  what: string;
  categories: string[];
  sitePrompt: string;
  fields: string[];
  useFor: string;
}
interface DispatchRow {
  id: string;
  target: string;
  status: "pending" | "sent" | "failed";
  title: string;
  externalRef: string | null;
  externalUrl: string | null;
  error: string | null;
  liveStatus: string | null;
  liveState: string | null;
}
export type SendSource = { itemId: string } | { noteId: string };
interface Form {
  title: string;
  description: string;
  priority: "P1" | "P2" | "P3" | "P4";
  due: string;
  assignees: string;
  site: string;
  category: string;
  company: string;
  amount: string;
}

interface PlaybookDef { key: string; label: string; description: string; steps: Array<{ target: string; job: string }> }
const usePlaybooks = () => useQuery({ queryKey: ["dispatch", "playbooks"], queryFn: () => api<{ items: PlaybookDef[] }>("/api/work/dispatch/playbooks"), staleTime: 300_000 });

export const useDispatchTargets = () =>
  useQuery({ queryKey: ["dispatch", "targets"], queryFn: () => api<{ items: DispatchTarget[] }>("/api/work/dispatch/targets"), staleTime: 300_000 });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emailsOf = (v: string) => v.split(/[\s,;]+/).filter(Boolean);
const blank = (title = "", description = ""): Form => ({ title, description, priority: "P3", due: "", assignees: "", site: "", category: "", company: "", amount: "" });
const COMPANIES = ["IFG", "IFP", "IFS", "IFT", "IFI", "MYT"];
const labelOf = (targets: DispatchTarget[] | undefined, key: string) => targets?.find((t) => t.key === key)?.label ?? key;

/** The dialog. `source` = a ONE task or note (AI suggests from it); without it the person writes a fresh brief. */
export function SendToDialog({ onClose, source, prefill }: { onClose: () => void; source?: SendSource; prefill?: { title?: string; description?: string } }) {
  const qc = useQueryClient();
  const t = useDispatchTargets();
  const [chosen, setChosen] = useState<string[]>([]);
  const [forms, setForms] = useState<Record<string, Form>>({});
  const [why, setWhy] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState(false);
  const [brief, setBrief] = useState("");
  const [playbook, setPlaybook] = useState<string | null>(null);
  const pbs = usePlaybooks();
  const ai = useMutation({
    mutationFn: (pb?: string | null) => post<{ items: Array<{ target: string; why: string; form: { title: string; description: string; priority: Form["priority"]; category: string | null; site: string | null; amount: number | null } }>; source: { title: string; private: string | null } | null }>("/api/work/dispatch/suggest", { source: source ?? null, text: brief || prefill?.title || undefined, playbook: pb ?? null }),
    onSuccess: (r, pb) => {
      setPlaybook(pb ?? null);
      if (!r.items.length) return;
      setChosen(r.items.map((i) => i.target));
      setWhy(Object.fromEntries(r.items.map((i) => [i.target, i.why])));
      setForms((f) => ({ ...f, ...Object.fromEntries(r.items.map((i) => [i.target, { ...blank(i.form.title, i.form.description), priority: i.form.priority, category: i.form.category ?? "", site: i.form.site ?? "", amount: i.form.amount != null ? String(i.form.amount) : "" }])) }));
    },
  });
  // Suggest straight away when sending an existing task/note
  useEffect(() => {
    if (source && t.data?.items.length && !ai.isPending && !ai.data && !ai.error) ai.mutate(null);
  }, [source, t.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const isPrivate = !!ai.data?.source?.private;
  const toggle = (key: string) => {
    setChosen((c) => (c.includes(key) ? c.filter((x) => x !== key) : [...c, key]));
    setForms((f) => (f[key] ? f : { ...f, [key]: blank(ai.data?.source?.title ?? prefill?.title ?? "", prefill?.description ?? "") }));
  };
  const set = (key: string, patch: Partial<Form>) => setForms((f) => ({ ...f, [key]: { ...f[key]!, ...patch } }));
  const bad = chosen.flatMap((k) => emailsOf(forms[k]?.assignees ?? "").filter((e) => !EMAIL_RE.test(e)));
  const ready = chosen.length > 0 && chosen.every((k) => forms[k]?.title.trim()) && !bad.length && (!isPrivate || confirm);
  const send = useMutation({
    mutationFn: () =>
      post<{ items: DispatchRow[] }>("/api/work/dispatch/send", {
        source: source ?? null,
        confirmShare: isPrivate ? confirm : undefined,
        playbook,
        items: chosen.map((k) => {
          const f = forms[k]!;
          const tg = t.data?.items.find((x) => x.key === k);
          return {
            target: k,
            title: f.title,
            description: f.description,
            priority: f.priority,
            dueAt: f.due ? new Date(`${f.due}T17:00:00`).toISOString() : null,
            assigneeEmails: tg?.fields.includes("assignees") ? emailsOf(f.assignees) : [],
            site: f.site || null,
            category: f.category || null,
            companyCode: tg?.fields.includes("company") && f.company ? f.company : null,
            amount: tg?.fields.includes("amount") && f.amount.trim() && !Number.isNaN(Number(f.amount)) ? Number(f.amount) : null,
          };
        }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dispatch"] }),
  });

  if (t.data && !t.data.items.length) {
    return (
      <Modal title="Send to…" onClose={onClose}>
        <p className="text-sm">No app is ready to receive work from ONE yet. Each app switches this on once its side is live.</p>
      </Modal>
    );
  }
  const done = send.data?.items;
  return (
    <Modal
      title="Send to…"
      wide
      onClose={onClose}
      footer={
        done ? (
          <div className="flex justify-end"><button className="btn btn-primary" onClick={onClose}>Close</button></div>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span className="muted mr-auto text-xs">Each app gets its own item; they show up under Linked work and in Tasks → In other apps.</span>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" disabled={!ready || send.isPending} onClick={() => send.mutate()}>
              <Send size={14} /> {send.isPending ? "Sending…" : chosen.length > 1 ? `Send to ${chosen.length} apps` : chosen.length ? `Send to ${labelOf(t.data?.items, chosen[0]!)}` : "Send"}
            </button>
          </div>
        )
      }
    >
      {done ? (
        <ul className="space-y-2 text-sm">
          {done.map((d) => (
            <li key={d.id}>
              <DispatchLine d={d} targets={t.data?.items} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-3">
          {!source && (
            <div className="flex flex-wrap items-center gap-2">
              <input className="input !w-auto flex-1 text-sm" placeholder="Describe the work, e.g. 'water leaking from the ceiling in apartment 4, Sliema'" value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={2000} />
              <button className="btn" disabled={!brief.trim() || ai.isPending} onClick={() => ai.mutate(null)}><Sparkles size={14} /> {ai.isPending ? "Thinking…" : "Suggest"}</button>
            </div>
          )}
          {source && ai.isPending && <p className="muted text-sm"><Sparkles size={13} className="inline" /> Working out where this should go…</p>}
          <ErrorNote error={ai.error} />
          {!!pbs.data?.items.length && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="muted">Playbooks:</span>
              {pbs.data.items.map((pb) => (
                <button key={pb.key} type="button" className={`btn btn-sm ${playbook === pb.key ? "btn-primary" : ""}`} title={`${pb.description}: ${pb.steps.map((s) => labelOf(t.data?.items, s.target)).join(" + ")}`}
                  disabled={ai.isPending || (!source && !brief.trim())} onClick={() => ai.mutate(pb.key)}>
                  <Sparkles size={13} /> {pb.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {t.data?.items.map((x) => (
              <label key={x.key} className={`btn cursor-pointer ${chosen.includes(x.key) ? "btn-primary" : ""}`} title={x.useFor}>
                <input type="checkbox" className="sr-only" checked={chosen.includes(x.key)} onChange={() => toggle(x.key)} />
                {x.label}
              </label>
            ))}
          </div>
          {chosen.map((k) => {
            const tg = t.data?.items.find((x) => x.key === k);
            const f = forms[k];
            if (!tg || !f) return null;
            return (
              <fieldset key={k} className="space-y-2 rounded-xl border border-[var(--line)] p-3">
                <legend className="px-1 text-sm font-semibold">{tg.label} <span className="muted font-normal">— {tg.what}{why[k] ? ` · ${why[k]}` : ""}</span></legend>
                <Field label="Title"><input className="input" value={f.title} onChange={(e) => set(k, { title: e.target.value })} maxLength={300} /></Field>
                <Field label="Details"><textarea className="input min-h-[4.5rem] font-[inherit] text-sm" value={f.description} onChange={(e) => set(k, { description: e.target.value })} maxLength={8000} /></Field>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {tg.fields.includes("priority") && <Field label="Priority">
                    <select className="input" value={f.priority} onChange={(e) => set(k, { priority: e.target.value as Form["priority"] })}>
                      <option value="P1">Urgent</option><option value="P2">High</option><option value="P3">Normal</option><option value="P4">Low</option>
                    </select>
                  </Field>}
                  {tg.fields.includes("dueAt") && <Field label={tg.key === "ai-crm" ? "Expected close" : tg.key === "ai-procurement" ? "Needed by" : tg.key === "fusionhire" ? "Closing date" : "Due"}><input className="input" type="date" value={f.due} onChange={(e) => set(k, { due: e.target.value })} /></Field>}
                  {tg.categories.length > 0 && (
                    <Field label="Category">
                      <select className="input" value={f.category} onChange={(e) => set(k, { category: e.target.value })}>
                        <option value="">Choose…</option>
                        {tg.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                  )}
                  <Field label={tg.sitePrompt}><input className="input" value={f.site} onChange={(e) => set(k, { site: e.target.value })} maxLength={200} /></Field>
                  {tg.fields.includes("company") && (
                    <Field label="Company">
                      <select className="input" value={f.company} onChange={(e) => set(k, { company: e.target.value })}>
                        <option value="">—</option>
                        {COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                  )}
                  {tg.fields.includes("amount") && (
                    <Field label="Amount (EUR)"><input className="input" inputMode="decimal" value={f.amount} onChange={(e) => set(k, { amount: e.target.value })} /></Field>
                  )}
                </div>
                {tg.fields.includes("assignees") && (
                  <Field label={tg.key === "fusionhire" ? "Hiring manager (their email)" : "Assign to (optional, their email)"}><input className="input" placeholder="name@infinite-fusion.com" value={f.assignees} onChange={(e) => set(k, { assignees: e.target.value })} /></Field>
                )}
              </fieldset>
            );
          })}
          {bad.length > 0 && <p className="text-xs text-red-700">Not an email address: {bad.join(", ")}</p>}
          {isPrivate && chosen.length > 0 && (
            <label className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <input type="checkbox" className="mt-1" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              This is {ai.data?.source?.private} in ONE. I understand that people using {chosen.map((k) => labelOf(t.data?.items, k)).join(" and ")} will see what I send.
            </label>
          )}
          <ErrorNote error={send.error} />
        </div>
      )}
    </Modal>
  );
}

/** Back-compat name used by the Tasks page. */
export const SendToAppDialog = SendToDialog;

function DispatchLine({ d, targets }: { d: DispatchRow; targets?: DispatchTarget[] }) {
  const label = labelOf(targets, d.target);
  const live = d.liveStatus ? ` · ${d.liveStatus}` : "";
  return (
    <div className="flex flex-wrap items-center gap-2">
      {d.liveState === "done" ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Link2 size={14} className="muted" />}
      <span className="font-medium">{label}</span>
      {d.status === "sent" && <span>{d.externalRef ?? "created"}{live}</span>}
      {d.status === "pending" && <span className="text-amber-700">sending… ({d.error ?? "waiting for the app"})</span>}
      {d.status === "failed" && <span className="text-red-700">not created: {d.error}</span>}
      <span className="muted truncate">{d.title}</span>
      {d.externalUrl && <a className="ml-auto inline-flex items-center gap-1 text-brand-600 hover:underline" href={d.externalUrl} target="_blank" rel="noreferrer">Open <ExternalLink size={12} /></a>}
      {d.status === "sent" && d.error && <span className="w-full text-xs text-amber-700">Note from {label}: {d.error}</span>}
    </div>
  );
}

/** "Send to…" button for a task or note page. Hidden until at least one app can receive. */
export function SendToButton({ source, className }: { source: SendSource; className?: string }) {
  const t = useDispatchTargets();
  const [open, setOpen] = useState(false);
  if (!t.data?.items.length) return null;
  return (
    <>
      <button className={className ?? "btn btn-sm"} onClick={() => setOpen(true)}><Send size={14} /> Send to…</button>
      {open && <SendToDialog source={source} onClose={() => setOpen(false)} />}
    </>
  );
}

/** "Linked work": what this task/note was sent to, with each app's live status. Offers to close the task when all are done. */
export function LinkedWork({ source }: { source: SendSource }) {
  const qc = useQueryClient();
  const t = useDispatchTargets();
  const url = "itemId" in source ? `/api/work/items/${source.itemId}/linked` : `/api/notes/${source.noteId}/linked`;
  const q = useQuery({ queryKey: ["dispatch", "linked", url], queryFn: () => api<{ items: DispatchRow[]; askClose: boolean }>(url), refetchInterval: 60_000 });
  const answer = useMutation({
    mutationFn: (close: boolean) => post(`/api/work/items/${(source as { itemId: string }).itemId}/linked/close`, { close }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  if (!q.data?.items.length) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Linked work</h3>
      <ul className="space-y-1.5 text-sm">{q.data.items.map((d) => <li key={d.id}><DispatchLine d={d} targets={t.data?.items} /></li>)}</ul>
      {q.data.askClose && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          <CheckCircle2 size={14} /> All linked work is done. Close this task?
          <button className="btn btn-sm btn-primary" disabled={answer.isPending} onClick={() => answer.mutate(true)}>Close task</button>
          <button className="btn btn-sm" disabled={answer.isPending} onClick={() => answer.mutate(false)}>Keep open</button>
          <ErrorNote error={answer.error} />
        </div>
      )}
    </div>
  );
}
